<template>
  <div class="insight-dev-mode-modal">
    <el-dialog
      v-model="isOpen"
      :show-close="false"
      width="520px"
      class="insight-dev-mode-dialog"
      :before-close="handleClose"
    >
      <template #header>
        <HeaderIaIcone title="Modo desenvolvedor" @close="handleClose" />
      </template>

      <div class="insight-dev-mode-modal__content">
        <el-tabs
          v-model="actionSelecionada"
          class="insight-dev-mode-modal__tabs"
        >
          <el-tab-pane
            v-for="action in actions"
            :key="action.value"
            :name="action.value"
          >
            <template #label>
              <span class="insight-dev-mode-modal__tab-label">
                {{ action.label }}
              </span>
            </template>

            <div
              v-if="action.value === EDevModeAction.GerarRelatorio"
              class="insight-dev-mode-modal__form"
            >
              <div class="insight-dev-mode-modal__form-heading">
                <p class="insight-dev-mode-modal__title">Gerar relatório</p>
                <p>
                  Escolha o modelo de DRE e o mês de competência do relatório.
                </p>
              </div>

              <div class="insight-dev-mode-modal__fields">
                <F360Select
                  v-model="modeloDreSelecionado"
                  :options="modelosDre"
                  :loading="loadingModelosDre"
                  :disabled="loadingModelosDre || !modelosDre.length"
                  label="Modelo de DRE"
                  full
                  placeholder="Selecione um modelo de DRE"
                />

                <F360DatePicker
                  v-model="periodoGeracaoSelecionado"
                  type="month"
                  format="MM/YYYY"
                  value-format="YYYY-MM"
                  :disabled-date="desabilitarMesAtualOuFuturo"
                  label="Mês do relatório"
                  full
                  placeholder="Selecione um mês"
                />
              </div>

              <div class="insight-dev-mode-modal__helper">
                <MaterialIcon icon="info" size="16px" />
                <span>
                  O mês atual e os meses futuros não ficam disponíveis para
                  seleção. O envio
                  será processado em background após a solicitação.
                </span>
              </div>
            </div>

            <div
              v-else
              class="insight-dev-mode-modal__form insight-dev-mode-modal__form--danger"
            >
              <div class="insight-dev-mode-modal__form-heading">
                <p class="insight-dev-mode-modal__title">
                  Excluir relatório selecionado?
                </p>
                <p>
                  O relatório do período abaixo será removido para o cliente
                  atual. Essa ação não exclui os dados financeiros de origem.
                </p>
              </div>

              <F360Select
                v-model="periodoSelecionado"
                :options="periodos"
                label="Selecione o período"
                full
                placeholder="Selecione um período"
              />
            </div>
          </el-tab-pane>
        </el-tabs>
      </div>

      <template #footer>
        <div class="insight-dev-mode-modal__footer">
          <F360Button base="neutral" @click="handleClose">
            Cancelar
          </F360Button>

          <F360Button
            v-if="actionSelecionada === EDevModeAction.GerarRelatorio"
            filled
            prefix-icon="description"
            :loading="loadingGerarRelatorio"
            :disabled="
              loadingModelosDre ||
              loadingGerarRelatorio ||
              !modeloDreSelecionado ||
              !periodoGeracaoSelecionado
            "
            @click="handleGerarRelatorio"
          >
            Gerar relatório
          </F360Button>

          <F360Button
            v-else
            custom-type="danger-full"
            :disabled="!periodoSelecionado || loading"
            :loading="loading"
            @click="handleExcluirRelatorio"
          >
            Excluir relatório
          </F360Button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { DITypes } from "@/core/@types/types";
import useNotification from "@/core/composables/notification/useNotification";
import F360Button from "@/ui/components/Buttons/Button/F360Button.vue";
import F360Select from "@/ui/components/Select/Base/F360Select.vue";
import F360DatePicker from "@/ui/components/DatePickers/DatePicker/F360DatePicker.vue";
import MaterialIcon from "@/ui/components/Icons/MaterialIcon/MaterialIcon.vue";
import type { IFinancasFeatureGateway } from "@/features/financas/services/ports/FinancasFeatureGateway";
import HeaderIaIcone from "../../components/HeaderIaIcone.vue";
import { diContainer } from "@/inversify.config";
import type { TOptSelect } from "@/features/financas/views/Indicadores/store/dashboard/types";
import type { InsightConfiguracaoGateway } from "../configuracao/services/InsightConfiguracaoGateway";
import { ref, watch } from "vue";

enum EDevModeAction {
  GerarRelatorio = "gerar-relatorio",
  ExcluirRelatorio = "excluir-relatorio"
}

type TDevModeActionOption = {
  value: EDevModeAction;
  label: string;
};

const props = defineProps<{
  gateway: InsightConfiguracaoGateway;
  periodos: TOptSelect[];
  periodoInicial?: string;
  modeloInicial?: string;
}>();

const emit = defineEmits<{
  (event: "relatorioExcluido"): void;
  (event: "fecharModal"): void;
}>();

const isOpen = defineModel<boolean>({ default: false });
const loading = ref(false);
const loadingModelosDre = ref(false);
const loadingGerarRelatorio = ref(false);
const actionSelecionada = ref<EDevModeAction>(EDevModeAction.GerarRelatorio);
const periodoSelecionado = ref("");
const periodoGeracaoSelecionado = ref("");
const modeloDreSelecionado = ref("");
const modelosDre = ref<TOptSelect[]>([]);
const { showNotification } = useNotification();

const financasGateway = diContainer.get<IFinancasFeatureGateway>(
  DITypes.FINANCAS_FEATURES
);

const actions: TDevModeActionOption[] = [
  {
    value: EDevModeAction.GerarRelatorio,
    label: "Gerar relatório"
  },
  {
    value: EDevModeAction.ExcluirRelatorio,
    label: "Excluir relatório"
  }
];

const obterPeriodoInicial = (opcoes: TOptSelect[]) => {
  if (
    props.periodoInicial &&
    opcoes.some((option) => option.value === props.periodoInicial)
  ) {
    return props.periodoInicial;
  }

  return opcoes[0]?.value || "";
};

const obterModeloInicial = (opcoes: TOptSelect[]) => {
  if (
    props.modeloInicial &&
    opcoes.some((option) => option.value === props.modeloInicial)
  ) {
    return props.modeloInicial;
  }

  return opcoes[0]?.value || "";
};

const obterMesAnterior = () => {
  const mesAnterior = new Date();
  mesAnterior.setDate(1);
  mesAnterior.setMonth(mesAnterior.getMonth() - 1);

  return `${mesAnterior.getFullYear()}-${String(mesAnterior.getMonth() + 1).padStart(2, "0")}`;
};

const normalizarMes = (value?: string) => value?.slice(0, 7) || "";

const desabilitarMesAtualOuFuturo = (date: Date) => {
  const hoje = new Date();

  return (
    date.getFullYear() > hoje.getFullYear() ||
    (date.getFullYear() === hoje.getFullYear() &&
      date.getMonth() >= hoje.getMonth())
  );
};

const obterMesInicial = () => {
  const periodoInicial = normalizarMes(props.periodoInicial);

  return periodoInicial &&
    !desabilitarMesAtualOuFuturo(new Date(`${periodoInicial}-01T00:00:00`))
    ? periodoInicial
    : obterMesAnterior();
};

const sincronizarPeriodos = () => {
  if (
    !props.periodos.some((option) => option.value === periodoSelecionado.value)
  ) {
    periodoSelecionado.value = obterPeriodoInicial(props.periodos);
  }

  if (
    !periodoGeracaoSelecionado.value ||
    desabilitarMesAtualOuFuturo(
      new Date(`${periodoGeracaoSelecionado.value}-01T00:00:00`)
    )
  ) {
    periodoGeracaoSelecionado.value = obterMesInicial();
  }
};

watch(
  () => [props.periodoInicial, props.periodos] as const,
  sincronizarPeriodos,
  { immediate: true }
);

watch(
  () => [props.modeloInicial, modelosDre.value] as const,
  () => {
    if (
      !modelosDre.value.some(
        (option) => option.value === modeloDreSelecionado.value
      )
    ) {
      modeloDreSelecionado.value = obterModeloInicial(modelosDre.value);
    }
  },
  { immediate: true }
);

const handleBuscarModelosDre = async () => {
  if (modelosDre.value.length || loadingModelosDre.value) return;

  try {
    loadingModelosDre.value = true;

    const response = await financasGateway.getModelosDREList();

    modelosDre.value =
      response?.Data?.map((modelo) => ({
        label: modelo.Nome,
        value: modelo._id
      })) || [];

    modeloDreSelecionado.value = obterModeloInicial(modelosDre.value);
  } catch (error) {
    console.error(error);
    showNotification("Não foi possível carregar os modelos de DRE", {
      type: "error"
    });
  } finally {
    loadingModelosDre.value = false;
  }
};

watch(isOpen, (isModalOpen) => {
  if (isModalOpen) {
    actionSelecionada.value = EDevModeAction.GerarRelatorio;
    sincronizarPeriodos();
    void handleBuscarModelosDre();
  }
});

const handleClose = () => {
  isOpen.value = false;
  emit("fecharModal");
};

const handleGerarRelatorio = async () => {
  if (
    loadingModelosDre.value ||
    loadingGerarRelatorio.value ||
    !modeloDreSelecionado.value ||
    !periodoGeracaoSelecionado.value
  ) {
    return;
  }

  try {
    loadingGerarRelatorio.value = true;

    const response = await props.gateway.gerarRelatorio({
      idModeloDRE: modeloDreSelecionado.value,
      dataDeCompetencia: `${periodoGeracaoSelecionado.value}-01`
    });

    if (response.isFailure) {
      showNotification(response.error || "Erro ao gerar relatório", {
        type: "error"
      });

      return;
    }

    showNotification("Relatório enviado para processamento", {
      type: "success"
    });
    handleClose();
  } catch (error) {
    console.error(error);
    showNotification("Erro ao gerar relatório", { type: "error" });
  } finally {
    loadingGerarRelatorio.value = false;
  }
};

const handleExcluirRelatorio = async () => {
  if (
    actionSelecionada.value !== EDevModeAction.ExcluirRelatorio ||
    !periodoSelecionado.value ||
    loading.value
  ) {
    return;
  }

  try {
    loading.value = true;

    const response = await props.gateway.limparRelatorio({
      DataDeCompetencia: periodoSelecionado.value
    });

    if (response.isFailure) {
      showNotification(response.error || "Erro ao excluir relatório", {
        type: "error"
      });

      return;
    }

    showNotification("Relatório excluído com sucesso", {
      type: "success"
    });
    emit("relatorioExcluido");
    handleClose();
  } catch (error) {
    console.error(error);
    showNotification("Erro ao excluir relatório", { type: "error" });
  } finally {
    loading.value = false;
  }
};
</script>

<style lang="scss">
.insight-dev-mode-modal {
  .financas-dialog {
    padding: 0;
  }

  .financas-dialog__header {
    padding-bottom: 0;
  }

  &__content {
    padding: 0 1rem 1rem;

    p {
      margin: 0;
      color: _map.get($map: $neutro, $key: 700);
      font-size: 14px;
      line-height: 20px;
    }
  }

  &__form-heading {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__title {
    color: _map.get($map: $neutro, $key: 900) !important;
    font-weight: 700;
  }

  &__tabs {
    margin: 0 0 1rem;

    :deep(.financas-tabs__header) {
      margin: 0;
      padding: 0 16px;
    }

    :deep(.financas-tabs__nav) {
      display: flex;
      width: 100%;
    }

    :deep(.financas-tabs__nav-wrap::after) {
      background-color: _map.get($map: $neutro, $key: 200);
    }

    :deep(.financas-tabs__nav-item) {
      display: flex;
      flex: 1;
      justify-content: center;
      height: 44px;
      padding: 0 16px;
      color: _map.get($map: $neutro, $key: 700);
      font-size: 14px;
      font-weight: 600;
      text-align: center;
    }

    :deep(.financas-tabs__nav-item.is-active) {
      color: _map.get($map: $azulPrincipal, $key: 700);
    }

    :deep(.financas-tabs__active-bar) {
      background-color: _map.get($map: $azulPrincipal, $key: 700);
    }

    :deep(.financas-tabs__content) {
      padding: 0 16px;
    }
  }

  &__tab-label {
    white-space: nowrap;
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 20px 0 4px;
  }

  &__form--danger {
    gap: 12px;
  }

  &__fields {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }

  &__helper {
    display: flex;
    align-items: center;
    gap: 6px;
    color: _map.get($map: $neutro, $key: 600);
    font-size: 12px;
    line-height: 16px;

    .sIcon {
      color: _map.get($map: $azulPrincipal, $key: 500);
    }
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 0 16px 16px;
  }
}

@media (max-width: 600px) {
  .insight-dev-mode-modal {
    &__fields {
      grid-template-columns: 1fr;
    }

    &__footer {
      flex-wrap: wrap;
    }
  }
}
</style>
